/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { differenceInDays } from 'date-fns';
import { useForm } from 'hooks/useForm';
import {
  createIncomeTransaction,
  getSingleIncomeTransaction,
  updateIncomeTransaction,
} from 'actions';
// import { removeEmptyProperties } from 'helpers/removeEmptyProperties';
import { excludeUnsetField } from 'components/utils/excludeUnsetField';
import { useFinancialCategory } from 'hooks/useFinancialCategory';
import { useExpenseCategory } from 'hooks/useExpenseCategory';
import {
  withTranslation,
  withCurrency,
  withPersonalInfo,
  withUserDefaultSpace,
  withCustomRouter,
  withContract,
  withMsgInfo,
} from 'components/hoc';
import { useOrgIndicator } from 'hooks/useOrgIndicator';
import { useAccountCategoryRef } from '../hooks/useAccountCategoryRef';
import { transactionPosting, getTransactionValuesFromPayload } from './utils';
import {
  Input,
  Select,
  InputDatePicker,
  Button,
  ButtonLink,
  TransactionForm,
} from 'components/common/FormItems';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';
import { useCurrencyRate } from 'hooks/useCurrencyRate';
import lodash from 'lodash';
import { getDateDistance, formCurrencyConverter } from '../finance-utils';
import { ORG_INDICATOR_TYPES, orgIndicatorOptions } from '../../../../../constants';

const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const DualInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  > div {
    min-width: 150px;
    flex: 1;
    margin-right: 1rem;

    :last-child {
      margin-right: 0px;
    }
  }
`;

const StyledSelect = styled(Select)`
  padding-right: 2rem;
`;

const StyledInput = styled(Input)`
  padding-right: 1.8rem;
`;

const StyledInputDescription = styled(Input)`
  padding-right: 1.5rem;
`;

const StyledInputDatePicker = styled(InputDatePicker)`
  padding-right: 2rem;
`;

const initialValues = {
  description: '',
  amount: '',
  currency: 'EUR',
  dc_indicator: 'C',
  contract: '',
  person_id: '',
  contract_item: '',
  expense_financial_account: '',
  asset_financial_account: 'bank',
  asset_account_category: 'asset',
  expense_account_category: 'expense',
  position_link: '',
  date: '',
  partner_org_ind: '',
  bpos_org_indicator: '',
  partner: '',
  rec_op_id: '',
  transaction: '',
  amount_sc: '',
};

const cardTypeOptions = [
  {
    id: 'income',
    long_description_deu: 'Income',
    long_description_eng: 'Income',
  },
  {
    id: 'expense',
    long_description_deu: 'Expense',
    long_description_eng: 'Expense',
  },
];

const consumptionFormSchema = {
  description: {
    validation: 'required',
  },
  amount: {
    validation: 'required',
  },
  amount_sc: {
    validation: 'required',
  },
  dc_indicator: {
    validation: 'required',
  },
  currency: {
    validation: 'required',
  },
  position_link: {
    validation: 'required',
  },

  expense_financial_account: {
    validation: 'required',
  },
  asset_financial_account: {
    validation: 'required',
  },
  date: {
    validation: 'required',
  },
  person_id: {
    validation: 'required',
  },
};

const ConsumptionForm = (props) => {
  const {
    tr,
    personalInfoOptions,
    userDefaultSpace,
    fn,
    basePath,
    routeParams,
    updateMode,
    detailMode,
    reverseMode,
    currencyLocaleOptions,
    contractInfoOptions,
    setMsgInfo,
  } = props;


  const { currencyRate } = useCurrencyRate();

  const routepath = props.location.pathname;
  let currentPath = routepath.split('/');

  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [hasLoadedItem, setHasLoadedItem] = useState(false);
  const [hasAppliedReverse, setHasAppliedReverse] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [isDateEntered, setIsDateEntered] = useState(false);

  const navigate = useNavigate();
  const { item } = useSelector((state) => state.transaction);
  const referenceCheckingAccount = useReferenceCheckingAccount();

  const DefaultSpaceCurrency = userDefaultSpace?.space_detail?.currency;

  // Process form data before submission
  const processFormData = (formValues) => {
    const processedData = { ...formValues };
    if (
      processedData.position_link?.startsWith('P_') ||
      processedData.position_link?.startsWith('O_')
    ) {
      processedData.position_link = processedData.position_link.substring(2);
    }
    return processedData;
  };

    const onSubmit = useCallback(
  async (formValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const processedFormValues = processFormData(formValues);
      // Ensure bpos_org_indicator is included even if empty
      if (processedFormValues.bpos_org_indicator === '') {
        processedFormValues.bpos_org_indicator = null;
      }

      if (!formValues.id) {
        // Create mode
        const payload = transactionPosting(
          excludeUnsetField(processedFormValues, initialData),
        );
        await dispatch(createIncomeTransaction(payload));
        setMsgInfo({
          success: true,
          msg: [`${tr('Transaction created successfully')}`],
        });
        navigate(basePath);
      } else if (reverseMode) {
        // Reverse mode - only send transaction_id, let backend copy items
        const reversalPayload = {
          transaction_id: formValues.id,
        };
        await dispatch(createIncomeTransaction(reversalPayload));
        setMsgInfo({
          success: true,
          msg: [`${tr('Transaction reversed successfully')}`],
        });
        navigate('/dashboard/data/finance/consumption');
      } else {
        // Update mode
        const payload = transactionPosting(
          excludeUnsetField(processedFormValues, initialData),
          'update'
        );
        await dispatch(updateIncomeTransaction(formValues.id, payload));
        setMsgInfo({
          success: true,
          msg: [`${tr('Transaction updated successfully')}`],
        });
        navigate(basePath);
      }

      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  },
  [isSubmitting, dispatch, tr, reverseMode, basePath, setMsgInfo, history, initialData],
);


  const {
    handleChange,
    handleBlur,
    touched,
    values,
    errors,
    setValues,
    handleSubmit,
  } = useForm({
    initialValues,
    validationSchema: consumptionFormSchema,
    onSubmit,
  });

  const handlePositionLinkChange = useCallback(
    (e) => {
      const selectedValue = e.target.value;
      // Check if it's a person or organisation selection
      if (selectedValue.startsWith('P_')) {
        setValues((prev) => ({
          ...prev,
          position_link: selectedValue,
          bpos_org_indicator: 'P',
        }));
      } else if (selectedValue.startsWith('O_')) {
        setValues((prev) => ({
          ...prev,
          position_link: selectedValue,
          bpos_org_indicator: 'O',
        }));
      } else {
        setValues((prev) => ({
          ...prev,
          position_link: selectedValue,
          bpos_org_indicator: null,
        }));
      }
    },
    [setValues],
  );

  // Initialize org indicator hook
  const { handleOrgIndicatorChange, organisationOrPersonOptions } = useOrgIndicator({
    defaultOrgIndicator: values.partner_org_ind || ORG_INDICATOR_TYPES.ORG,
    onOrgIndicatorChange: useCallback(
      (value) => {
        setValues((prev) => ({
          ...prev,
          partner_org_ind: value,
          partner: '',
        }));
      },
      [setValues],
    ),
  });

  const { id: transactionId } = routeParams;


// handleChange function to clear amount when date changes
const handleDateChange = useCallback(
  (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];
    
    // Prevent future dates
    if (selectedDate > today) {
      setMsgInfo({
        success: false,
        msg: [tr('Transaction date cannot be in the future')],
      });
      return;
    }
    
    handleChange(e);
    // If date is being changed and amount exists, clear the amount
    if (values.amount && selectedDate !== values.date) {
      setValues((prev) => ({
        ...prev,
        amount: '',
        amount_sc: '',
      }));
    }
  },
  [handleChange, values.amount, values.date, setValues, setMsgInfo, tr],
);

// const handleDateChange = useCallback(
//   (e) => {
//     handleChange(e);
//     // If date is being changed and amount exists, clear the amount
//     if (values.amount && e.target.value !== values.date) {
//       setValues((prev) => ({
//         ...prev,
//         amount: '',
//         amount_sc: '',
//       }));
//     }
//   },
//   [handleChange, values.amount, values.date, setValues],
// );


  useEffect(() => {
    setHasLoadedItem(false);
    if (transactionId) {
      setValues(initialValues);
    }
  }, [transactionId]);

// useEffect to monitor date changes
useEffect(() => {
  setIsDateEntered(!!values.date);
}, [values.date]);


  // Fetch transaction data
  useEffect(() => {
    if (transactionId && !formLoading) {
      setFormLoading(true);
      try {
        const { ...data } = dispatch(getSingleIncomeTransaction(transactionId));
        setValues({ data });
      } finally {
        setFormLoading(false);
      }
    }
  }, [transactionId]);

  // Load item data
  useEffect(() => {
    if (item && item.id && !hasLoadedItem && item.id === parseInt(transactionId)) {
      setHasLoadedItem(true);

      const formValues = {};
      const payload = getTransactionValuesFromPayload(item);

      // Copy all fields from payload
      Object.keys(initialValues).forEach((key) => {
        formValues[key] = payload[key];
      });

      if (payload.date) {
        formValues.date = payload.date;
      }

      if (['receive', 'payable'].includes(payload.asset_financial_account)) {
        let positionLinkId = null;

        if (item.transaction_item && item.transaction_item.length > 0) {
          const assetItem = item.transaction_item.find(
            (ti) =>
              ti.financial_account === payload.asset_financial_account &&
              ti.account_category === 'asset',
          );

          if (assetItem && assetItem.position_link) {
            positionLinkId = assetItem.position_link;
          }
        }

        if (!positionLinkId && payload.position_link) {
          positionLinkId = payload.position_link;
        }

        if (positionLinkId) {
          positionLinkId = String(positionLinkId);

          if (payload.bpos_org_indicator === 'P') {
            formValues.position_link = `P_${positionLinkId}`;
            formValues.bpos_org_indicator = 'P';
          } else if (payload.bpos_org_indicator === 'O') {
            formValues.position_link = `O_${positionLinkId}`;
            formValues.bpos_org_indicator = 'O';
          }
        }
      } else if (payload.asset_financial_account === 'cash') {
        // formValues.position_link = String(payload.amount);
        formValues.position_link = String(payload.currency || payload.amount);

      } else if (payload.asset_financial_account === 'bank') {
        let bankPositionLink = null;
        if (item.transaction_item && item.transaction_item.length > 0) {
          const assetItem = item.transaction_item.find(
            (ti) =>
              ti.financial_account === 'bank' && ti.account_category === 'asset',
          );
          if (assetItem && assetItem.position_link) {
            bankPositionLink = assetItem.position_link;
          }
        }

        if (!bankPositionLink) {
          bankPositionLink = payload.position_link;
        }

        if (bankPositionLink) {
          const isAccountNumber = referenceCheckingAccount.some(
            (acc) => acc.account_no === String(bankPositionLink),
          );

          if (isAccountNumber) {
            formValues.position_link = String(bankPositionLink);
            formValues.position_link_display = String(bankPositionLink);
        
          } else {
            const bankPositionLinkId = parseInt(bankPositionLink);
            const matchingBankAccount = referenceCheckingAccount.find(
              (acc) =>
                acc.id === bankPositionLinkId ||
                acc.id === String(bankPositionLink) ||
                acc.id === bankPositionLink,
            );

            if (matchingBankAccount) {
              formValues.position_link = matchingBankAccount.account_no;
              formValues.position_link_display = matchingBankAccount.account_no;
            } else {
              formValues.position_link = String(bankPositionLink);
              formValues.position_link_display = String(bankPositionLink);
            }
          }
        }
      } else {
        formValues.position_link = payload.position_link
          ? String(payload.position_link)
          : '';
      }

      // Ensure bpos_org_indicator is set
      if (!formValues.bpos_org_indicator && payload.bpos_org_indicator) {
        formValues.bpos_org_indicator = payload.bpos_org_indicator;
      }

      // Set partner org indicator
      if (payload.partner_org_ind) {
        formValues.partner_org_ind = payload.partner_org_ind;
        handleOrgIndicatorChange(payload.partner_org_ind);
      }

      // Set partner
      if (payload.partner) {
        formValues.partner = String(payload.partner);
      }
      setInitialData({ ...formValues, id: item.id });
      setValues({ ...formValues, id: item.id });
    }
  }, [
    item?.id,
    hasLoadedItem,
    referenceCheckingAccount,
    handleOrgIndicatorChange,
    transactionId,
  ]);


 // Handle reverse mode
  useEffect(() => {
    // Reset hasLoadedItem when item changes or doesn't exist
    setHasLoadedItem(false);
  }, [item?.id, item?.updated_at]);


  useEffect(() => {
    if (reverseMode && item && values.amount && values.id === item.id) {
      const currentAmount = parseFloat(values.amount);
      const currentAmountSc = parseFloat(values.amount_sc);
      
      if (!isNaN(currentAmount) && currentAmount > 0) {
        setValues(prev => ({
          ...prev,
          amount: (-Math.abs(currentAmount)).toFixed(2),
          amount_sc: (-Math.abs(currentAmountSc || 0)).toFixed(2)
        }));
      }
    }
  }, [reverseMode, item?.id, values.id, values.amount, values.amount_sc, setValues]);
  


  useEffect(() => {
    setHasAppliedReverse(false);
  }, [transactionId]);

  const isError = (touchedObj, error) =>
    touchedObj && error?.length > 0 ? '  ' : '';

  const { financialCategoryOptions } = useFinancialCategory();
  const expenseCategoryOptions = useExpenseCategory();
  const validAccounts = ['credit', 'bank', 'cash', 'payable', 'receive'];

  const validAccountsFinancialCategoryOptions = financialCategoryOptions.filter(
    ({ id }) => validAccounts.includes(id),
  );

  const transactionCategoryOptions = useAccountCategoryRef({
    financialAccountCat: values.asset_financial_account,
    amount: values.amount,
    currency: values.currency,
    rec_op_id: values.rec_op_id,
  }).filter((option) => {
    if (
      values.asset_financial_account === 'payable' ||
      values.asset_financial_account === 'receive'
    ) {
      return (
        option.type === 'organisation' ||
        (option.type == 'person' && option.originalId !== Number(values.person_id))
      );
    } else {
      return option;
    }
  });

  // Memoize currency rate calculation
  const allCurrencyRate = useMemo(() => {
    return lodash.compact(
      Object.keys(currencyRate).map((data) => currencyRate[data]),
    );
  }, [currencyRate]);

  const closestDate = useCallback((selectedDate, allRates) => {
    if (!selectedDate || !allRates || allRates.length === 0) {
      return null;
    }

    const dateSelected = selectedDate.split('-');
    const selectedYear = dateSelected[0];
    const selectedMonth = dateSelected[1];
    const LAST_AVAILABLE_DATE = '2011-01-01';
    const LAST_FETCHED_MONTHLY_DATE = '2021-05-01';
    const PERIOD_BEFORE_DAILY_RATES = '2021-05-31';
    const PERIOD_OF_DAILY_RATES = '2021-06-01';

    //compose date range
    if (selectedDate < LAST_AVAILABLE_DATE) {
      return null;
    } else if (
      selectedDate >= LAST_FETCHED_MONTHLY_DATE &&
      selectedDate < PERIOD_BEFORE_DAILY_RATES
    ) {
      const closestRate = allRates.find(
        (rate) => rate.date === LAST_FETCHED_MONTHLY_DATE,
      );

      return closestRate;
    } else if (selectedDate >= PERIOD_OF_DAILY_RATES) {
      const closestRate = allRates.find((rate) => rate.date === selectedDate);

      if (closestRate) {
        return closestRate;
      } else {
        const previouslyAvailableRates = allRates.filter(
          (rate) => rate.date <= selectedDate,
        );
        const latestAvailableRate = previouslyAvailableRates.pop();

        return latestAvailableRate;
      }
    } else {
      const selectedDateObj = new Date(selectedDate);

      if (parseInt(selectedMonth) === 1) {
        const FIRST_DAY = '01';
        const nearestPastYear = parseInt(selectedYear) - 1;
        const NEAREST_PAST_MONTH = 12;
        const nearestFutureMonth = parseInt(selectedMonth) + 1;

        const composedPreviousDate = [
          `${nearestPastYear}`,
          `${NEAREST_PAST_MONTH}`,
          `${FIRST_DAY}`,
        ].join('-');

        const composedFutureDate = [
          `${selectedYear}`,
          `0${nearestFutureMonth}`,
          `${FIRST_DAY}`,
        ].join('-');

        const distancePast = differenceInDays(selectedDateObj, 
          new Date(composedPreviousDate),
          'days',
        );
        const distanceFuture = differenceInDays(new Date(composedFutureDate), 
          selectedDateObj,
          'days',
        );

        const closestRate = getDateDistance(
          distancePast,
          distanceFuture,
          allRates,
          composedPreviousDate,
          composedFutureDate,
        );

        return closestRate;
      } else if (parseInt(selectedMonth) === 12) {
        const FIRST_DAY = '01';
        const NEAREST_FUTURE_MONTH = '01';
        const nearestPastMonth = parseInt(selectedMonth) - 1;

        const composedPreviousDate = [
          `${selectedYear}`,
          `${nearestPastMonth}`,
          `${FIRST_DAY}`,
        ].join('-');

        const composedFutureDate = [
          `${selectedYear}`,
          `${NEAREST_FUTURE_MONTH}`,
          `${FIRST_DAY}`,
        ].join('-');

        const distancePast = differenceInDays(selectedDateObj, 
          new Date(composedPreviousDate),
          'days',
        );
        const distanceFuture = differenceInDays(new Date(composedFutureDate), 
          selectedDateObj,
          'days',
        );

        const closestRate = getDateDistance(
          distancePast,
          distanceFuture,
          allRates,
          composedPreviousDate,
          composedFutureDate,
        );

        return closestRate;
      } else {
        const FIRST_DAY = '01';
        const nearestFutureMonth = parseInt(selectedMonth) + 1;

        const composedPreviousDate = [
          `${selectedYear}`,
          `${selectedMonth}`,
          `${FIRST_DAY}`,
        ].join('-');

        const composedFutureDate = [
          `${selectedYear}`,
          `0${nearestFutureMonth}`,
          `${FIRST_DAY}`,
        ].join('-');

        const distancePast = differenceInDays(selectedDateObj, 
          new Date(composedPreviousDate),
          'days',
        );
        const distanceFuture = differenceInDays(new Date(composedFutureDate), 
          selectedDateObj,
          'days',
        );

        const closestRate = getDateDistance(
          distancePast,
          distanceFuture,
          allRates,
          composedPreviousDate,
          composedFutureDate,
        );

        return closestRate;
      }
    }
  }, []);

  // Calculate space currency with debounce
  const [debouncedValues, setDebouncedValues] = useState({
    date: values.date,
    amount: values.amount,
    currency: values.currency,
  });

  // Debounce value changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValues({
        date: values.date,
        amount: values.amount,
        currency: values.currency,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [values.date, values.amount, values.currency]);

  // Calculate and update amount_sc
  useEffect(() => {
    if (
      debouncedValues.date &&
      debouncedValues.amount &&
      DefaultSpaceCurrency &&
      allCurrencyRate.length > 0
    ) {
      const closestRate = closestDate(debouncedValues.date, allCurrencyRate);

      if (closestRate) {
        const converted = formCurrencyConverter(
          debouncedValues.amount,
          closestRate,
          DefaultSpaceCurrency,
          debouncedValues,
        );

        // Round to 2 decimal places
        // const roundedConverted = Math.round(converted * 100) / 100;
        const roundedConverted = parseFloat((Math.round(converted * 100) / 100).toFixed(2));

        if (
          !isNaN(roundedConverted) &&
          Math.abs(roundedConverted - (values.amount_sc || 0)) > 0.01
        ) {
          setValues((prev) => ({ ...prev, amount_sc: roundedConverted }));
        }
      }
    }
  }, [debouncedValues, DefaultSpaceCurrency, allCurrencyRate.length]);

  useEffect(() => {
    // Auto-set position_link to currency when asset_financial_account is 'cash'
    if (values.asset_financial_account === 'cash' && values.currency) {
      setValues((prev) => ({
        ...prev,
        position_link: values.currency,
      }));
    }
  }, [values.asset_financial_account, values.currency, setValues]);

  useEffect(() => {
    // Clear amount_sc when amount is cleared
    if (!values.amount || values.amount === '') {
      if (values.amount_sc) {
        setValues((prev) => ({ ...prev, amount_sc: '' }));
      }
    }
  }, [values.amount, setValues]);

  return (
    <Wrapper className="form-wrapper">
      <div className="flex">
        <StyledButtonLink
          to={basePath}
          className="option-btn"
          icon="angle double left"
          name="All Entries"
        />
      </div>
      <div className="row">
        <div
          className="col-7 ml-10 mt-30 container"
          style={{
            position: 'relative',
            background: 'var(--admincat-color-grey-1)',
          }}
        >
          <br />

          <TransactionForm
            onSubmit={handleSubmit}
            formData={values}
            title={tr('Consumption')}
            isLoading={isSubmitting}
            validationSchema={consumptionFormSchema}
            renderActionButtons={() => null}
            editButtonMarginRight="3.8rem"
            editButtonGap="1rem"
          >
            <StyledInputDatePicker
              id="date"
              name="date"
              label="Transaction date"
              value={values.date}
              onChange={handleDateChange}
              error={isError(touched?.date, errors?.date?.[0])}
              showRequiredAsterisk
              disabled={updateMode && true}
              maxDate={new Date()}
            />

            <DualInputWrapper>
              <Input
                label="Amount"
                name="amount"
                id="amount"
                // placeholder={isDateEntered ? "Enter amount" : "Please select date first"}
                value={values.amount}
                placeholder="Enter amount"
                onChange={handleChange}
                showRequiredAsterisk
                // disabled={updateMode && true}
                disabled={!isDateEntered || (updateMode && true)}
                fieldType="number"
              />
              <StyledSelect
                label="Currency"
                showRequiredAsterisk
                name="currency"
                optionDefaultValue={tr('Currency')}
                value={values.currency}
                onChange={handleChange}
                localeOptions={currencyLocaleOptions}
                onBlur={handleBlur}
                disabled={!isDateEntered || (updateMode && true)}
                // disabled={updateMode && true}
                error={isError(touched?.currency, errors?.currency?.[0])}
              />
            </DualInputWrapper>

            <DualInputWrapper>
              <Input
                label="Amount in Space Currency"
                name="amount_sc"
                value={fn(values.amount_sc)}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled
                // disabled={updateMode && true}  
              />
              <StyledSelect
                label="Space Currency"
                name="currency"
                value={DefaultSpaceCurrency}
                onChange={handleChange}
                localeOptions={currencyLocaleOptions}
                disabled
              />
            </DualInputWrapper>

            <StyledInputDescription
              label="Description"
              showRequiredAsterisk
              name="description"
              id="description"
              value={values.description}
              placeholder="Description"
              onChange={handleChange}
            />

            <StyledSelect
              label="Person"
              showRequiredAsterisk
              optionDefaultValue="Choose Person"
              value={values.person_id}
              className="w-80"
              id="person"
              name="person_id"
              options={personalInfoOptions}
              onChange={handleChange}
              onBlur={handleBlur}
              error={isError(touched?.person_id, errors?.person_id?.[0])}
            />

            <StyledSelect
              label={tr('Transaction partner indicator')}
              value={values?.partner_org_ind}
              name="partner_org_ind"
              id="title"
              optionDefaultValue={tr('Choose Organisation or Person')}
              localeOptions={orgIndicatorOptions}
              onChange={(e) => {
                handleChange(e);
                handleOrgIndicatorChange(e.target.value);
              }}
              sort={false}
            />

            <StyledSelect
              label={tr('Partner')}
              value={values?.partner}
              name="partner"
              localeOptions={organisationOrPersonOptions}
              onChange={handleChange}
              optionDefaultValue={tr('Partner')}
              disabled={!values.partner_org_ind}
            />

            <StyledSelect
              label={tr('Contract')}
              value={values?.contract}
              name="contract"
              options={contractInfoOptions}
              onChange={handleChange}
              onBlur={handleBlur}
              optionDefaultValue={tr('Choose Contract')}
            />

            <DualInputWrapper>
              <Select
                label="Category"
                name="account_category"
                id="account_category"
                optionDefaultValue={tr('Expense')}
                value={values.account_category}
                onChange={handleChange}
                localeOptions={cardTypeOptions}
                onBlur={handleBlur}
                disabled
              />
              <StyledSelect
                label="Account (+)"
                name="expense_financial_account"
                optionDefaultValue={tr('Expense Category')}
                value={values.expense_financial_account}
                onChange={handleChange}
                localeOptions={expenseCategoryOptions}
                onBlur={handleBlur}
                showRequiredAsterisk
                error={isError(
                  touched?.expense_financial_account,
                  errors?.expense_financial_account?.[0],
                )}
              />
            </DualInputWrapper>

            <DualInputWrapper>
              <Select
                label="Account (-)"
                showRequiredAsterisk
                id="asset_financial_account"
                name="asset_financial_account"
                value={values.asset_financial_account}
                onChange={handleChange}
                localeOptions={validAccountsFinancialCategoryOptions}
                onBlur={handleBlur}
                placeholder="Account (-)"
                error={isError(
                  touched?.asset_financial_account,
                  errors?.asset_financial_account?.[0],
                )}
              />

              {detailMode && values.asset_financial_account === 'bank' ? (
                <StyledInput
                  label={tr('Bank Account Number')}
                  name="position_link_display"
                  value={values.position_link_display}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={detailMode}
                />
              ) : (
                <StyledSelect
                  label="Position-ID"
                  optionDefaultValue={tr('Position')}
                  name="position_link"
                  value={values.position_link}
                  onChange={
                    ['receive', 'payable'].includes(values.asset_financial_account)
                      ? handlePositionLinkChange
                      : handleChange
                  }
                  options={transactionCategoryOptions || []}
                  onBlur={handleBlur}
                  showRequiredAsterisk
                  error={isError(touched?.position_link, errors?.position_link?.[0])}
                  disabled={values.asset_financial_account === 'cash' || detailMode}
                />
              )}
            </DualInputWrapper>

            <Input
              // label={tr('Bpos Org. Indicator')}
              type="hidden"
              name="bpos_org_indicator"
              onChange={handleChange}
              value={values.bpos_org_indicator || ''}
            />

            {currentPath.includes('create') && (
              <div className="row">
                <div className="col-md-12 d-flex">
                  <Button
                    style={{ width: '20%' }}
                    actionButton
                    type="submit"
                    name={tr('Post')}
                  />
                </div>
              </div>
            )}

            {(reverseMode || updateMode) && (
              <div className="row">
                <div className="col-md-12 d-flex">
                  <Button
                    style={{ width: '20%' }}
                    name={reverseMode ? 'Reverse' : updateMode ? 'Save' : 'Post'}
                    actionButton
                    type="submit"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  />
                </div>
              </div>
            )}
          </TransactionForm>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withCurrency,
  withPersonalInfo,
  withUserDefaultSpace,
  withCustomRouter,
  withContract,
  withMsgInfo,
)(ConsumptionForm);
